import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Activity, AlertCircle, CheckCircle2, XCircle, Clock,
  DollarSign, RefreshCw, Loader2, ArrowLeft,
} from "lucide-react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";

const verdictConfig = {
  healthy: { color: "text-emerald-500", bg: "bg-emerald-500/10", border: "border-emerald-500/30", icon: CheckCircle2 },
  degraded: { color: "text-amber-500", bg: "bg-amber-500/10", border: "border-amber-500/30", icon: AlertCircle },
  critical: { color: "text-red-500", bg: "bg-red-500/10", border: "border-red-500/30", icon: XCircle },
  unknown: { color: "text-muted-foreground", bg: "bg-secondary/50", border: "border-border", icon: Clock },
  no_credentials: { color: "text-muted-foreground", bg: "bg-secondary/50", border: "border-border", icon: XCircle },
};

export default function DataForSeoHealth() {
  const qc = useQueryClient();

  const { data: health, isLoading: healthLoading } = useQuery({
    queryKey: ["dfs-health"],
    queryFn: async () => (await api.get("/dataforseo/health")).data,
    refetchInterval: 15_000,
  });

  const { data: credits } = useQuery({
    queryKey: ["dfs-credits"],
    queryFn: async () => (await api.get("/dataforseo/credits")).data,
    refetchInterval: 60_000,
  });

  const { data: callsData, isLoading: callsLoading } = useQuery({
    queryKey: ["dfs-calls"],
    queryFn: async () => (await api.get("/dataforseo/calls?limit=100")).data,
    refetchInterval: 15_000,
  });

  const testMutation = useMutation({
    mutationFn: async () => (await api.post("/dataforseo/test")).data,
    onSuccess: (data) => {
      if (data.ok) {
        toast.success("DataForSEO API antwortet korrekt!");
      } else {
        toast.error(`API-Test fehlgeschlagen: ${data.note}`);
      }
      qc.invalidateQueries({ queryKey: ["dfs-health"] });
      qc.invalidateQueries({ queryKey: ["dfs-calls"] });
    },
  });

  const verdict = health?.verdict || "unknown";
  const cfg = verdictConfig[verdict] || verdictConfig.unknown;
  const VerdictIcon = cfg.icon;

  const calls = callsData?.calls || [];
  const balance = credits?.balance_usd;
  const spent = credits?.money_spent;

  return (
    <div className="space-y-8 max-w-6xl" data-testid="dataforseo-health-page">
      <div className="flex items-center gap-4">
        <Link to="/settings" className="text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" />
        </Link>
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">DataForSEO Health</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Live-Status der Ranking-API. Auto-Refresh alle 15 Sekunden.
          </p>
        </div>
      </div>

      {/* Verdict Card */}
      <div
        className={cn("border rounded-lg p-6", cfg.bg, cfg.border)}
        data-testid="verdict-card"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <VerdictIcon className={cn("h-8 w-8 mt-1", cfg.color)} />
            <div>
              <div className={cn("text-2xl font-semibold", cfg.color)}>
                {health?.verdict_label || "Lädt…"}
              </div>
              <div className="text-sm text-muted-foreground mt-1">
                {verdict === "healthy" && "Alle Rankings kommen zuverlässig von DataForSEO."}
                {verdict === "degraded" && "Einige API-Calls schlagen fehl. Rankly nutzt teilweise Mock-Fallback."}
                {verdict === "critical" && "Die meisten Calls schlagen fehl. Rankly-Zahlen sind aktuell UNZUVERLÄSSIG."}
                {verdict === "unknown" && "Warte auf erste API-Calls, dann kann Rankly den Status bestimmen."}
                {verdict === "no_credentials" && "DataForSEO-Zugangsdaten fehlen in .env"}
              </div>
            </div>
          </div>
          <Button
            onClick={() => testMutation.mutate()}
            disabled={testMutation.isPending}
            variant="outline"
            className="gap-2 shrink-0"
            data-testid="test-connectivity-btn"
          >
            {testMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Activity className="h-4 w-4" />
            )}
            API jetzt testen
          </Button>
        </div>
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          label="Erfolgsrate 24h"
          value={healthLoading ? "…" : `${health?.stats_24h?.success_rate_pct ?? 0}%`}
          hint={`${health?.stats_24h?.successful ?? 0} von ${health?.stats_24h?.total ?? 0} Calls`}
          testid="stat-success-rate"
        />
        <StatCard
          label="Ø Antwortzeit"
          value={healthLoading ? "…" : `${health?.stats_24h?.avg_duration_ms ?? 0}ms`}
          hint="pro API-Call"
          testid="stat-avg-duration"
        />
        <StatCard
          label="Credits verbleibend"
          value={balance == null ? "—" : `$${balance.toFixed(2)}`}
          hint={spent != null ? `$${spent} verbraucht gesamt` : "—"}
          testid="stat-credits"
          icon={DollarSign}
        />
        <StatCard
          label="Fehlgeschlagen 24h"
          value={healthLoading ? "…" : (health?.stats_24h?.failed ?? 0).toString()}
          hint="Fallback auf Mock-Daten"
          testid="stat-failed"
          tone={((health?.stats_24h?.failed ?? 0) > 0) ? "text-amber-500" : undefined}
        />
      </div>

      {/* Last Success & Failure */}
      <div className="grid md:grid-cols-2 gap-4">
        <EventCard
          title="Letzter Erfolg"
          event={health?.last_success}
          tone="emerald"
          testid="last-success-card"
        />
        <EventCard
          title="Letzter Fehler"
          event={health?.last_failure}
          tone="red"
          testid="last-failure-card"
        />
      </div>

      {/* Call History Table */}
      <div className="border border-border rounded-lg overflow-hidden" data-testid="calls-history-table">
        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold">API-Call History</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Letzte 100 Calls · Auto-Refresh
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              qc.invalidateQueries({ queryKey: ["dfs-calls"] });
              qc.invalidateQueries({ queryKey: ["dfs-health"] });
            }}
          >
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/30">
              <tr className="border-b border-border">
                <th className="text-left px-4 py-2 text-[10px] uppercase tracking-wider font-mono text-muted-foreground">Zeit</th>
                <th className="text-left px-4 py-2 text-[10px] uppercase tracking-wider font-mono text-muted-foreground">Status</th>
                <th className="text-left px-4 py-2 text-[10px] uppercase tracking-wider font-mono text-muted-foreground">Keyword</th>
                <th className="text-right px-4 py-2 text-[10px] uppercase tracking-wider font-mono text-muted-foreground">Location</th>
                <th className="text-right px-4 py-2 text-[10px] uppercase tracking-wider font-mono text-muted-foreground">Dauer</th>
                <th className="text-left px-4 py-2 text-[10px] uppercase tracking-wider font-mono text-muted-foreground">Note</th>
              </tr>
            </thead>
            <tbody>
              {callsLoading && (
                <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">Lädt…</td></tr>
              )}
              {!callsLoading && calls.length === 0 && (
                <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">Noch keine API-Calls</td></tr>
              )}
              {calls.map((c, i) => (
                <tr key={i} className="border-b border-border/60 last:border-0 hover:bg-secondary/40">
                  <td className="px-4 py-2 font-mono text-xs">
                    {new Date(c.timestamp).toLocaleTimeString("de-DE")}
                  </td>
                  <td className="px-4 py-2">
                    <span className={cn(
                      "px-2 py-0.5 rounded-full text-[10px] font-mono uppercase",
                      c.success
                        ? "bg-emerald-500/15 text-emerald-500"
                        : "bg-red-500/15 text-red-500"
                    )}>
                      {c.success ? "OK" : "FAIL"}
                    </span>
                  </td>
                  <td className="px-4 py-2 max-w-[280px] truncate">{c.keyword}</td>
                  <td className="px-4 py-2 text-right font-mono text-xs">{c.location_code}</td>
                  <td className="px-4 py-2 text-right font-mono text-xs">{c.duration_ms}ms</td>
                  <td className="px-4 py-2 text-xs text-muted-foreground max-w-[400px] truncate">
                    {c.note || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, hint, testid, tone, icon: Icon }) {
  return (
    <div className="border border-border rounded-lg p-4 bg-card" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground">
          {label}
        </div>
        {Icon && <Icon className="h-3.5 w-3.5 text-muted-foreground" />}
      </div>
      <div className={cn("text-2xl font-semibold tabular-nums", tone)}>{value}</div>
      {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
    </div>
  );
}

function EventCard({ title, event, tone, testid }) {
  const isEmerald = tone === "emerald";
  return (
    <div
      className={cn(
        "border rounded-lg p-4",
        isEmerald ? "border-emerald-500/30 bg-emerald-500/5" : "border-red-500/30 bg-red-500/5"
      )}
      data-testid={testid}
    >
      <div className={cn(
        "text-[10px] uppercase tracking-wider font-mono mb-2",
        isEmerald ? "text-emerald-500" : "text-red-500"
      )}>
        {title}
      </div>
      {!event && <div className="text-sm text-muted-foreground">— Keine Daten —</div>}
      {event && (
        <>
          <div className="text-sm font-medium truncate mb-1">{event.keyword}</div>
          <div className="text-xs text-muted-foreground">
            {new Date(event.timestamp).toLocaleString("de-DE")} · {event.duration_ms}ms
          </div>
          {event.note && (
            <div className="text-xs text-muted-foreground mt-2 line-clamp-2">
              {event.note}
            </div>
          )}
        </>
      )}
    </div>
  );
}
